import { supabase, TABLES } from "../lib/supabase";
import type { Database } from "../pages/types/supabase";

export type EmailAuditStatus =
  | "queued"
  | "sent"
  | "delivered"
  | "delivery_delayed"
  | "bounced"
  | "complained"
  | "opened"
  | "clicked"
  | "failed"
  | "unknown";

export type EmailAuditSource =
  | "contract_api"
  | "daily_report_resend"
  | "process_report_queue"
  | "reports_unknown"
  | "unknown";

export type EmailAuditFilterStatus = EmailAuditStatus | "all";
export type EmailAuditFilterSource = EmailAuditSource | "all";

export type EmailMessageRow =
  Database["public"]["Tables"]["email_messages"]["Row"];
export type EmailMessageEventRow =
  Database["public"]["Tables"]["email_message_events"]["Row"];

export interface EmailMessagesPageFilters {
  page: number;
  pageSize: number;
  searchTerm?: string;
  source?: EmailAuditFilterSource;
  status?: EmailAuditFilterStatus;
  dateFrom?: string;
  dateTo?: string;
}

export interface EmailMessagesPageResult {
  data: EmailMessageRow[];
  total: number;
}

const escapeLikeTerm = (value: string): string =>
  value.replace(/[%_]/g, "\\$&").replace(/,/g, "\\,");

export async function getEmailMessagesPage(
  filters: EmailMessagesPageFilters
): Promise<EmailMessagesPageResult> {
  const page = Math.max(1, filters.page);
  const pageSize = Math.max(1, filters.pageSize);
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from(TABLES.EMAIL_MESSAGES)
    .select("*", { count: "exact" });

  if (filters.searchTerm && filters.searchTerm.trim() !== "") {
    const escaped = escapeLikeTerm(filters.searchTerm.trim());
    query = query.or(
      `recipient_email.ilike.%${escaped}%,subject.ilike.%${escaped}%,provider_message_id.ilike.%${escaped}%`
    );
  }

  if (filters.source && filters.source !== "all") {
    query = query.eq("source", filters.source);
  }

  if (filters.status && filters.status !== "all") {
    query = query.eq("status", filters.status);
  }

  if (filters.dateFrom) {
    query = query.gte("created_at", `${filters.dateFrom}T00:00:00.000Z`);
  }

  if (filters.dateTo) {
    const toDate = new Date(`${filters.dateTo}T00:00:00.000Z`);
    toDate.setUTCDate(toDate.getUTCDate() + 1);
    query = query.lt("created_at", toDate.toISOString());
  }

  const { data, error, count } = await query
    .order("created_at", { ascending: false })
    .range(from, to);

  if (error) {
    console.error("Error fetching email audit page:", error);
    return { data: [], total: 0 };
  }

  return {
    data: data || [],
    total: count ?? 0,
  };
}

export async function getEmailMessageEvents(
  providerMessageId: string
): Promise<EmailMessageEventRow[]> {
  if (!providerMessageId) {
    return [];
  }

  const { data, error } = await supabase
    .from(TABLES.EMAIL_MESSAGE_EVENTS)
    .select("*")
    .eq("provider_message_id", providerMessageId)
    .order("provider_created_at", { ascending: true, nullsFirst: false })
    .order("received_at", { ascending: true });

  if (error) {
    console.error("Error fetching email events:", error);
    return [];
  }

  return data || [];
}
